import { z } from 'zod';
import { userChannelSchema, userRoleSchema } from '@logistics/contracts';
import type { WireUser, WireUserChannel, WireUserRole } from '@logistics/contracts';
import { toSourceAccess } from './source-access';

/**
 * The "Allow a user" form (design L648–692) as a zod schema, with the design's
 * own error messages. The API checks all of it again; this only saves a round
 * trip. The Telegram channel needs the numeric id because the bot matches the
 * sender by id — a username can change (docs/adr/0001, decision 10).
 */
const TELEGRAM_ID = /^\d{5,20}$/;
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const userFormSchema = z
  .object({
    name: z.string().trim().min(1, 'Name is required.'),
    telegram: z.string().trim(),
    telegramId: z.string().trim(),
    email: z.string().trim(),
    role: userRoleSchema,
    channels: z.array(userChannelSchema),
    sourceMode: z.enum(['all', 'only']),
    sourceIds: z.array(z.string()),
  })
  .superRefine((form, ctx) => {
    const problem = crossFieldProblem(form);
    if (problem !== undefined) ctx.addIssue({ code: 'custom', ...problem });
  });

export type UserForm = z.input<typeof userFormSchema>;
export type UserValues = z.output<typeof userFormSchema>;

export const EMPTY_USER: UserForm = {
  name: '',
  telegram: '',
  telegramId: '',
  email: '',
  role: 'dispatcher',
  channels: ['telegram'],
  sourceMode: 'all',
  sourceIds: [],
};

type Trimmed = Omit<UserForm, 'name'>;

/** The first rule broken across fields, and the field to hang it on. */
function crossFieldProblem(form: Trimmed): { path: [string]; message: string } | undefined {
  const at = (path: string, message: string) => ({ path: [path] as [string], message });
  if (form.telegram === '' && form.telegramId === '' && form.email === '') {
    return at('telegram', 'Provide a Telegram username or ID, or an email.');
  }
  if (form.channels.length === 0) return at('channels', 'Pick at least one channel.');
  if (form.telegramId !== '' && !TELEGRAM_ID.test(form.telegramId)) {
    return at('telegramId', 'A Telegram user ID is 5–20 digits.');
  }
  if (form.email !== '' && !EMAIL.test(form.email)) {
    return at('email', 'Enter a valid email address.');
  }
  if (form.channels.includes('telegram') && form.telegramId === '') {
    return at('telegramId', 'The Telegram channel needs a numeric Telegram user ID.');
  }
  if (form.channels.includes('web') && form.email === '') {
    return at('email', 'The Web channel needs an email.');
  }
  return sourceProblem(form);
}

/** "Only selected" with nothing ticked would leave them reading nothing at all. */
function sourceProblem(form: Trimmed): { path: [string]; message: string } | undefined {
  if (form.role === 'admin' || form.sourceMode === 'all' || form.sourceIds.length > 0) {
    return undefined;
  }
  return { path: ['sourceIds'], message: 'Pick at least one source, or allow all sources.' };
}

/** The one message the form shows, or nothing: the design has a single error line. */
export function validateUser(form: UserForm): string | undefined {
  const result = userFormSchema.safeParse(form);
  return result.success ? undefined : result.error.issues[0]?.message;
}

const FIELD_ORDER = ['name', 'telegram', 'telegramId', 'email', 'channels', 'sourceIds'] as const;

/** The first of react-hook-form's field errors, in the order the fields are laid out. */
export function firstFieldError(
  errors: Partial<Record<string, { message?: string | undefined } | undefined>>,
): string | undefined {
  for (const field of FIELD_ORDER) {
    const message = errors[field]?.message;
    if (message !== undefined) return message;
  }
  return undefined;
}

/** What `POST /users` is sent: blanks left out, the username without its "@". */
export function toNewUser(form: UserValues): {
  name: string;
  email?: string;
  telegramId?: string;
  telegramUsername?: string;
  role: WireUserRole;
  channels: WireUserChannel[];
  sourceAccess: string[] | null;
} {
  const telegram = form.telegram.replace(/^@/, '');
  return {
    name: form.name,
    ...(form.email === '' ? {} : { email: form.email }),
    ...(form.telegramId === '' ? {} : { telegramId: form.telegramId }),
    ...(telegram === '' ? {} : { telegramUsername: telegram }),
    role: form.role,
    channels: [...form.channels],
    // An admin reads every source; a list on one would only mislead.
    sourceAccess: form.role === 'admin' ? null : toSourceAccess(form.sourceMode, form.sourceIds),
  };
}

/** Toggling a channel chip on or off. */
export function toggleChannel(
  channels: readonly WireUserChannel[],
  channel: WireUserChannel,
): WireUserChannel[] {
  return channels.includes(channel)
    ? channels.filter((c) => c !== channel)
    : [...channels, channel];
}

/** The TELEGRAM column: "@handle" and "· 512884190", or a dash. */
export function telegramCell(user: WireUser): { handle: string; id: string } {
  return {
    handle: user.telegramUsername === undefined ? '—' : `@${user.telegramUsername}`,
    id: user.telegramId === undefined ? '' : `· ${user.telegramId}`,
  };
}

export const CHANNEL_LABELS: Readonly<Record<WireUserChannel, string>> = {
  telegram: 'Telegram',
  web: 'Web',
};

export const ROLES: readonly WireUserRole[] = ['admin', 'dispatcher', 'viewer'];
